/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div>
    <h2
      id="summary-nextsteps-nav"
      class="sr-only"
    >
      Recording evaluation navigation
    </h2>
    <nav aria-labelledby="summary-nextsteps-nav">
      <ul
        id="tips-nav-tabs"
        class="nav nav-tabs"
      >
        <li
          v-if="recording.results.diarizedTranscript || recording.diarizedTranscript"
          :class="{ active: tab === 'transcript' }"
        >
          <a
            id="transcriptlink"
            ref="transcriptlink"
            href="#"
            :aria-current="tab === 'transcript' ? 'true' : 'false'"
            @click.prevent="loadTab('transcript')"
            @keypress="loadTab('transcript', $event)"
          >
            Transcript
          </a>
        </li>
        <li
          v-if="!recording.diarizedTranscript && !recording.results.isConversational"
          :class="{ active: tab === 'next steps' }"
        >
          <a
            id="nextstepslink"
            ref="nextstepslink"
            href="#"
            :aria-current="tab === 'next steps' ? 'true' : 'false'"
            @click.prevent="loadTab('next steps')"
            @keypress="loadTab('next steps', $event)"
          >
            Next Steps
          </a>
        </li>
        <li
          v-if="!recording.diarizedTranscript"
          :class="tab === 'summary' ? 'active last-of-type' : 'last-of-type'"
        >
          <a
            id="summarylink"
            ref="summarylink"
            href="#"
            :aria-current="tab === 'summary' ? 'true' : 'false'"
            @click.prevent="loadTab('summary')"
            @keypress="loadTab('summary', $event)"
          >
            Summary
          </a>
        </li>
        <li
          tabindex="0"
          onclick="window.print()"
          class="print-link"
          @keypress="printUnlessTab"
        >
          <span
            class="print-container"
          >
            <svg class="svgicon icon-print">
              <use xlink:href="/static/svg-symbols.svg#icon-print" />
            </svg>
          </span>
        </li>
      </ul>
    </nav>
    <div
      class="tips"
    >
      <div
        v-if="speakerTips && speakerTips.main"
        id="improvement-tips"
        :class="{ hidden: tab !== 'next steps' }"
        aria-labelledby="nextstepsheader"
      >
        <h2
          id="nextstepsheader"
          ref="nextstepsheader"
          class="sr-only"
        >
          Next Steps
        </h2>
        <p v-if="needMostWorkOn">
          <span class="focus-area">
            You need the most work on {{ needMostWorkOn }}...
          </span><br>
        </p>
        <p v-if="!needMostWorkOn">
          Wow... you hit the ideal ranges in all dials!
        </p>
        <p v-for="tip in speakerTips.main.tips">
          {{ tip }}
        </p>
      </div>
      <div
        id="summary-tips-parent"
        :class="{ hidden: tab !== 'summary' }"
        aria-labelledby="summaryheader"
      >
        <h2
          id="summaryheader"
          ref="summaryheader"
          class="sr-only"
        >
          Recording scores and summary
        </h2>
        <div
          id="summary-tips"
        >
          <a
            id="scoreBookmarkForScreenReader"
            href="#"
            class="sr-only"
          >
            Your Scores Are Listed Below
          </a>
          <div
            v-for="(title, key) in keyToTitle"
            class="metricContainer"
          >
            <span
              v-if="key == 'wordSpotting'"
              class="dont-print sr-only"
            >
              <h3
                :id="key + 'SummaryHeader'"
                class="summary-header"
              >
                {{ title }}
                <span class="print-inline">
                  :&nbsp;&nbsp;
                </span>
              </h3>
              <br class="dont-print">
              <span>
                {{ (getScore(key, title)) ?
                  `Word Spotting located the following words: ${getScore(key, title)}` :
                  'Great job. No filler words were used.' }}
              </span>
            </span>
            <span
              v-else
              :class="(key === 'positiveVibes' || key === 'negativeVibes') ? 'dont-print' : ''"
            >
              <h3
                :id="key + 'SummaryHeader'"
                class="summary-header"
              >
                {{ title }}
                <span
                  class="print-inline"
                >
                  :&nbsp;&nbsp;
                </span>
              </h3>
              <br class="dont-print">
              <span class="sr-only">
                {{ getScore(key, title) }}
              </span>
              {{ key === 'questions' ? getScore(key, title) : '' }}
              <span v-html="getTipDataContent(key, title)" />
            </span>
          </div>
        </div>
      </div>
      <div
        id="transcript-parent"
        :class="{ hidden: tab !== 'transcript' }"
        aria-labelledby="transcriptheader"
      >
        <h2
          id="transcriptheader"
          ref="transcriptheader"
          class="sr-only"
        >
          Speech Transcript
        </h2>
        <div
          v-if="recording.diarizedTranscript || recording.results.diarizedTranscript"
          id="diarized-transcript"
        >
          <diarized-transcript :recording="recording" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
  @import '~bootstrap/less/variables';
  @import '../../less/bootstrap-vars';

  h2.summary-header {
    display: inline;
    font-size: 14px;
    font-weight: bold;
    margin: 0;
    padding: 0;
  }

  #transcript-parent, #summary-tips-parent {
    max-height: 230px;
    overflow-y: scroll;
    scrollbar-color: #666 #ffffff;
  }
  @media print {
    #transcript-parent, #summary-tips-parent {
      max-height: 50000px;
    }
  }

  #improvement-tips {
    b.heres-some-tips { display:none; }
    b.heres-some-tips:first { display:inline; }
  }
  .focus-area {
    font-weight:bold;
    font-size: 12pt;
  }
  .tips {
    border:1px solid @nav-tabs-border-color;
    border-top: none;
    > div {
      padding:10px;

      &.summary {
        .custom-scrollbar(300px);
      }
    }
  }
  .print-link {
    cursor:pointer;
    color: #333;
    margin-left: -5px;
    span {
      position: relative;
      bottom: 5px;
    }
  }
  .svgicon {
    display: inline-block;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
  }
  .icon-print {
    width: 19px; height: 19px;
    fill: #333;
    position: relative;
    bottom: 2px;
  }
</style>

<style lang="less" scoped>
  #tips-nav-tabs {
    li {
      width: 80px;
      a {
        padding: 5px;
      }
    }
  }
  .metricContainer {
    margin-bottom: 12px;
  }
</style>

<script>
import util from '../utils/util';
import DiarizedTranscript from '../forms/DiarizedTranscript';
import ChartHelper from '../utils/ChartHelper';

export default {
  components: {DiarizedTranscript},
  mixins: [ChartHelper],
  props: [ 'tips', 'recording', 'speakerNum', 'wavesurfer' ],
  data() {
    return {
      tipEventsLoaded: {},
      pendingEvents: [],
      keyToTitle: this.recording.results.isConversational ?
        {
          strengthOfOpening: 'Strength of Opening',
          pace: 'Pace',
          clarity: 'Clarity',
          talkVsListen: 'Talking versus Listening',
          convoSwitches: 'Meaningful Conversation Switches',
          questions: 'Number of Questions',
          wordSpotting: 'Word Spotting',
          positiveVibes: 'Positive Vibes',
          negativeVibes: 'Negative Vibes',
        } :
        {
          overall: 'Vocal Delivery',
          strengthOfOpening: 'Strength of Opening',
          clarity: 'Clarity',
          pace: 'Pace',
          pauseToTalk: 'Pausing',
          upspeak: 'Upspeak',
          varietyVolume: 'Variety of Volume',
          varietyPace: 'Variety of Pace',
          varietyPitch: 'Variety of Pitch',
          wordSpotting: 'Word Spotting',
          positiveVibes: 'Positive Vibes',
          negativeVibes: 'Negative Vibes',
        },
      tab: this.recording.diarizedTranscript || this.recording.results.isConversational ?
        'transcript' : 'next steps',
    };
  },
  computed: {
    speakerTips: {
      cache: false,
      get() {
        return (this.results && this.results.tips) ? this.results.tips : null;
      }
    },
    needMostWorkOn() {
      if (this.speakerTips && this.speakerTips.main && this.speakerTips.main.workOn.length) {
        if (this.speakerTips.main.workOn[0] === 'fillerWords') {
          return 'Filler Words';
        }
        return this.keyToTitle[this.speakerTips.main.workOn[0]];
      }
      return '';
    },
    isSmallViewport: {
      cache: false,
      get: util.isSmallViewport
    },
    summary() {
      const summary = {};
      this._.forOwn(this.keyToTitle, (title, key) => {
        if (this.speakerTips[key]) {
          summary[title] = this.speakerTips[key];
        }
      });
      return summary;
    }
  },
  watch: {
    recording() {
      this.tab = this.recording.diarizedTranscript || this.recording.results.isConversational ?
        'transcript' : 'next steps';
    }
  },
  destroyed() {
    // Prevent the events from being loaded twice
    this.pendingEvents.forEach((evt) => window.clearTimeout(evt));
  },
  methods: {
    getTipDataContent(key) {
      let tipData = this._getTooltipContentAndEvents(this.recording, key, 'inline', 'span');

      // Prevent the events from being loaded twice
      if (!this.tipEventsLoaded[key]) {
        const evt = window.setTimeout(() => {
          // Register the events
          tipData.onShown();
        }, 500);
        this.pendingEvents.push(evt);
      }

      this.tipEventsLoaded[key] = true;
      return tipData.content;
    },
    getScore(key, title) {
      if (!this.results || !this.results.scores || this.results.scores.isEmptyResults) return false;

      let score;

      switch (key) {
      case 'overall':
      case 'strengthOfOpening':
      case 'clarity':
        score = `Your ${title} score was ${this.results.scores[key]} out of 10.`;
        break;
      case 'talkVsListen':
        score = `You were talking ${this.results.scores[key]}% of the time.`;
        break;
      case 'convoSwitches':
        score = `There were a total of ${this.results.scores[key]} meaningful conversation switches.`;
        break;
      case 'questions':
        score = `${this.results.scores[key]} questions were detected in your speech.`;
        break;
      case 'upspeak':
        if (this.results.scores[key] < this.configs[key].idealMin) {
          score = 'You did not use any upspeak.';
        } else if (this.results.scores[key] > this.configs[key].idealMax) {
          score = 'You used too much upspeak.';
        } else {
          score = 'You used some upspeak.';
        }
        break;
      case 'pace':
      case 'pauseToTalk':
        if (key === 'upspeak') {
        } else {
          if (this.results.scores[key] < this.configs[key].idealMin) {
            score = `Your use of ${title} was ${this.configs[key].lowText}.`;
          } else if (this.results.scores[key] > this.configs[key].idealMax) {
            score = `Your use of ${title} was ${this.configs[key].highText}.`;
          } else {
            score = `Your use of ${title} was in the ideal range.`;
          }
        }
        break;
      case 'wordSpotting':
        _.forEach(this.results.scores.wordSpotting, (count, word) => {
          if (!count) return;

          const time = (count === 1) ? 'time' : 'times';
          score += `You used ${word} ${count} ${time}. `;
        });
        break;
      case 'positiveVibes':
      case 'negativeVibes':
        score = `The various ${title} are all scored from 0 to 10. Your scores are as follows. `;
        this.configs[key].activeVibes.forEach((vibe) => {
          score += `${vibe} is ${this.results.scores.vibes[vibe]}. `;
        });
        break;
      }

      return score;
    },
    loadTab(identifier, evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        // Firefox bug fix
        return true;
      }

      this.tab = identifier;

      const link = this.$refs[identifier.replace(/ /g, '') + 'link'];
      link.blur();
      this.$nextTick(() => {
        const header = document.getElementById(identifier.replace(/ /g, '') + 'header');
        if (header) {
          header.focus();
        }
      });

      this.$announcer.set(`The ${identifier} tab has loaded and is now available below.`);
    },
    setHotSpots(hotSpots) {
      this.hotSpots = hotSpots;
    },
    printUnlessTab(evt) {
      if (evt && evt.key && evt.key === 'Tab') {
        return true;
      }
      window.print();
    }
  }
};
</script>
